/**
 * What the MD Results tab does in the 3D view: find a job's structure, select
 * and zoom to residues named by their BMaps keys, and load a final pose as a
 * compound. Kept out of integrations/md.js so that module stays free of the
 * viewer for its unit tests.
 */
import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsCmds';
import { zoomToAtoms } from 'BMapsSrc/MainCanvas';
import { MolDataSource, MoleculeLoadOptions } from 'BMapsSrc/utils';
import { residueKeyOfAtom } from 'BMapsSrc/integrations/md';
import { MdSimulationJob } from 'BMapsSrc/jobman/jobs/MdSimulationJob';

/**
 * The structure (caseData) a job ran on: the loaded structure with the job's
 * structure name, else the structure of a loaded compound the job simulated.
 * @param {object} job - redux job record
 * @param {object} [stash] - the stash returned with the results
 * @returns {object|null}
 */
export function caseDataForJob(job, stash) {
    const name = stash?.structure?.name || job?.requestmeta?.structure;
    if (name) {
        const found = App.Workspace.allCaseData().find((cd) => cd.mapCase?.displayName === name);
        if (found) return found;
    }
    const scope = stash || MdSimulationJob.scopeFromRequestMeta(job);
    const [cmpd] = MdSimulationJob.candidateCompounds(scope);
    return cmpd ? (App.getDataParents(cmpd)?.caseData || null) : null;
}

/**
 * Select the residues with these keys ("CHAIN:RESNUM[ICODE]") on the job's
 * structure and zoom the view onto them.
 * @param {object} job
 * @param {object} stash
 * @param {string[]} keys
 * @returns {Promise<{ found?: number, missing?: string[], error?: string }>}
 */
export async function showResidues(job, stash, keys) {
    const caseData = caseDataForJob(job, stash);
    if (!caseData) return { error: 'The structure this job ran on is not loaded.' };
    const byKey = new Map();
    for (const residue of caseData.getResidues()) {
        const atom = residue.getAtoms()[0];
        if (atom) byKey.set(residueKeyOfAtom(atom), residue);
    }
    const residues = keys.map((k) => byKey.get(k)).filter(Boolean);
    const missing = keys.filter((k) => !byKey.has(k));
    if (residues.length === 0) return { error: `Residue${keys.length === 1 ? '' : 's'} not found: ${keys.join(', ')}` };
    await UserActions.Select(residues.map((r) => r.resSpec).join(' or '), caseData);
    const atoms = new Set(residues.flatMap((r) => r.getAtoms()));
    zoomToAtoms((a) => atoms.has(a));
    return { found: residues.length, missing };
}

/** A compound name the server accepts: letters, digits, '_' and '-'. */
export function poseCompoundName(baseName, copy) {
    const base = String(baseName || 'ligand').replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
    return `${base || 'ligand'}_md${copy}`;
}

/**
 * Load a copy's final ligand pose (already in the structure's frame) as a new
 * compound on the job's structure, coordinates kept as they are.
 * @param {object} job
 * @param {object} stash
 * @param {string} name - the compound's name in the packet (its resSpec)
 * @param {number} copy
 * @param {string} sdf - the copy's final_pose.ligand_sdf
 * @returns {Promise<{ compound?: object, error?: string }>}
 */
export async function loadFinalPose(job, stash, name, copy, sdf) {
    const caseData = caseDataForJob(job, stash);
    if (!caseData) return { error: 'The structure this job ran on is not loaded.' };
    const original = MdSimulationJob.candidateCompounds(
        stash || MdSimulationJob.scopeFromRequestMeta(job),
    ).find((c) => c.resSpec === name);
    const baseName = original?.getName?.() || original?.displayName || name;
    const molSource = new MolDataSource({
        sourceType: 'MolecularDynamics',
        sourceId: `${job.jobid}_${name}_${copy}`,
        compoundName: poseCompoundName(baseName, copy),
        molFormat: 'sdf',
        molData: sdf,
    });
    const { compounds, errors } = await UserActions.LoadMolData(
        molSource, new MoleculeLoadOptions({ retainCoords: true, alreadyMinimized: true }),
        caseData, { skipHeritage: true },
    );
    if (!compounds?.length) {
        return { error: (errors && errors.length) ? errors.join('; ') : 'The pose could not be loaded.' };
    }
    return { compound: compounds[0] };
}
